import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAllLocations,
  fetchAdminLocations,
  fetchAdminLocationDetail,
  exportLocation,
  type LocationListParams,
} from '@/lib/api/admin-locations'
import { downloadJson } from '@/lib/download'
import { buildExportFileName } from '@/lib/exportFileName'

export const locationKeys = {
  all: ['admin-locations'] as const,
  list: () => [...locationKeys.all, 'list'] as const,
  browse: (params: LocationListParams) => [...locationKeys.all, 'browse', params] as const,
  detail: (locationId: string | undefined) => [...locationKeys.all, 'detail', locationId] as const,
}

/** Every location, unpaginated — the dashboard map plots all of them. */
export function useAdminLocations() {
  return useQuery({
    queryKey: locationKeys.list(),
    queryFn: fetchAllLocations,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAdminLocationList(params: LocationListParams = {}) {
  return useQuery({
    queryKey: locationKeys.browse(params),
    queryFn: () => fetchAdminLocations(params),
    staleTime: 60 * 1000,
  })
}

export function useAdminLocationDetail(locationId: string | undefined) {
  return useQuery({
    queryKey: locationKeys.detail(locationId),
    queryFn: () => fetchAdminLocationDetail(locationId!),
    enabled: !!locationId,
  })
}

/** Downloads the location + all-layouts JSON bundle. */
export function useExportLocation() {
  return useMutation({
    mutationFn: ({ locationId, includeDeleted }: { locationId: string; includeDeleted?: boolean }) =>
      exportLocation(locationId, { includeDeleted }),
    onSuccess: (bundle) => {
      downloadJson(
        bundle,
        buildExportFileName(
          'location',
          String(bundle.location?.name ?? 'location'),
          String(bundle.location?.id ?? ''),
          new Date(),
        ),
      )
      toast.success(
        `Location exported (${bundle.counts.layouts} layout${bundle.counts.layouts === 1 ? '' : 's'})`,
      )
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Export failed'),
  })
}
